export const ALM_RELEASES = [
  {
    id: "wk11",
    label: "Week 11",
    date: "Dec 15 - 21",
    version: "v0.1",
    theme: "Crawl Phase: Architecture & Taxonomy",
    description:
      "Initial setup of the catalog structure and defining the metadata tags for Role and Phase.",
    changes: [
      "Defined catalog taxonomy (Role > Phase > Product)",
      "Drafted AE Onboarding Learning Path shell",
      "Created standard 'Product Module' template",
    ],
    artifacts: [
      {
        id: "catalog",
        title: "Catalog Architecture",
        type: "Wireframe",
        visualType: "catalog",
        status: "sandbox",
        description:
          "How a user browses content. Shows the filter logic for Roles and Topics.",
        almContext:
          "This artifact represents the foundational structure of the ALM catalog. It is designed to ensure learners see only role-relevant content.",

        // --- SECTION: PREREQUISITES (BLOCKERS) ---
        prerequisites: [
          {
            title: "Define Strategic Product List",
            status: "blocked", // renders as red X
            desc: "Leadership must agree on the 15-20 products that get built. Do not attempt to build for 2,600 SKUs.",
          },
          {
            title: "Approve Role Priorities",
            status: "done",
            desc: "David confirmed AE and Specialist are the primary audiences for V1.",
          },
        ],

        // --- SECTION: LEADERSHIP FRAMEWORK TOOL ---
        framework: {
          title: "Strategic Product Selection Framework",
          intro:
            "Use this framework to guide leadership away from 'coverage' and toward 'impact'.",
          steps: [
            {
              label: "1. Frame the Decision",
              text: "We are not deciding what matters overall. We are deciding which products require baseline education to support revenue.",
            },
            {
              label: "2. Set Criteria (Pick 4)",
              text: "Revenue Impact • Strategic Growth • Complexity • Launch Priority",
            },
            {
              label: "3. The 'Trap' Warning",
              text: "Remind leadership: If everything is taught, nothing is retained. Target 5-8 products per role for onboarding.",
            },
          ],
        },

        almSteps: [
          {
            label: "Create top-level Role tags",
            desc: "Account Executive, Specialist",
          },
          {
            label: "Create Phase tags",
            desc: "Onboarding, Continuous Learning, Launch",
          },
          {
            label: "Apply Product Category tags",
            desc: "Only for strategic products. Do not create tags for all products.",
          },
          {
            label: "Configure Catalog filters",
            desc: "Default view filtered by Role. Optional filters by Topic / Phase.",
          },
          {
            label: "Validate with test account",
            desc: "Confirm catalog is navigable and no 'content sprawl' view.",
          },
        ],
        leadershipBrief:
          "Strategic Product Scope Defined: We aligned with leadership on a focused Strategic Product List based on revenue impact and growth priority. Product Education will build standardized modules only for these products, ensuring onboarding remains scalable. This avoids content sprawl while supporting near-term business goals.",
      },
      {
        id: "lp_ae",
        title: "Learning Path: AE Onboarding",
        type: "Structure",
        visualType: "learning_path",
        status: "ready",
        description:
          "The sequential container for an Account Executive's first 30 days.",
        almContext:
          "This learning program defines the Product Education boundary. It contains only product knowledge, leaving sales execution for Sales Enablement to layer on later.",
        almSteps: [
          {
            label: "Create Learning Program",
            desc: "Name: 'AE Onboarding – First 30 Days'. Owner: Product Education.",
          },
          {
            label: "Add structure",
            desc: "Week 1: Fundamentals (mandatory). Month 1: Deep Dives (locked until Week 1 complete).",
          },
          {
            label: "Populate content",
            desc: "Use Core Product Modules (template-based). No role-play or sales execution content.",
          },
          {
            label: "Assignment",
            desc: "Auto-assign to AE role group. Completion tracked, not graded.",
          },
        ],
        leadershipBrief:
          "The AE Onboarding (First 30 Days) shell is built. It strictly segregates Product Knowledge (my scope) from Sales Skills (Revin's scope), creating a clean handoff point for the applied training layer.",
      },
      {
        id: "course_template",
        title: "Template: Product Module",
        type: "Standard",
        visualType: "course_page",
        status: "implemented",
        description:
          "The reusable layout for every single product page (Video + PDF + Quiz).",
        almContext:
          "A standardized course template ensures that every product module looks and behaves consistently, allowing launches to scale without custom rebuilds.",
        almSteps: [
          {
            label: "Create Course template",
            desc: "Video (≤ 20 mins), Resource (PDF/Slide), Quiz (80% pass).",
          },
          {
            label: "Define Required fields",
            desc: "Product name, Role relevance, Phase.",
          },
          {
            label: "Save as Reusable Template",
            desc: "Clone for each strategic product.",
          },
          {
            label: "Enforce Standard",
            desc: "Do NOT customize structure per product.",
          },
        ],
        leadershipBrief:
          "Standard 'Product Module' template is live. This ensures all future launches (including Trevi) use the exact same format, reducing build time by ~40% and ensuring consistent learner experience.",
      },
    ],
  },
  {
    id: "wk12",
    label: "Week 12 (Planned)",
    date: "Dec 22 - 28",
    version: "v0.2",
    theme: "Trevi Launch & Certification",
    description:
      "Integrating the Trevi product launch and defining the certification logic.",
    changes: [
      "Added 'New Launch' ribbon logic",
      "Created Certification logical object",
    ],
    artifacts: [
      {
        id: "trevi_mod",
        title: "Launch Module: Trevi",
        type: "Prototype",
        visualType: "trevi_launch",
        status: "sandbox",
        description:
          "How the Trevi launch appears inside the existing catalog.",
        almContext:
          "Demonstrates that new launches plug into the system rather than creating standalone 'Trevi Universities'. Resolves scope creep before it happens.",
        almSteps: [
          {
            label: "Clone Product Module Template",
            desc: "Ensure consistency.",
          },
          {
            label: "Apply 'New Launch' ribbon",
            desc: "Visually distinguish new content.",
          },
          {
            label: "Add Trevi-specific content",
            desc: "Video, One-Pager, Pricing Sheet.",
          },
          {
            label: "Insert module",
            desc: "Into AE Onboarding and Continuous Learning Catalog.",
          },
          {
            label: "Set Expiration",
            desc: "Expire 'New Launch' badge after 45 days.",
          },
        ],
        leadershipBrief:
          "Trevi launch architecture is confirmed. We are treating it as a standard module injected into the existing curriculum, rather than a standalone program. This sets the precedent for all 2026 launches.",
      },
      {
        id: "cert_logic",
        title: "Certification Logic",
        type: "Logic",
        visualType: "certification",
        status: "sandbox",
        description: "Rules for unlocking the 'Expert' badge.",
        almContext:
          "Clearly defines 'working knowledge vs expert' without confusing it with Sales Skills certifications.",
        almSteps: [
          {
            label: "Define Certification",
            desc: "Name: 'Product Expert – [Domain]'. Owner: Product Ed.",
          },
          {
            label: "Set Requirements",
            desc: "Completion of required Product Modules + Passing quizzes.",
          },
          {
            label: "Explicit Exclusions",
            desc: "No sales pitch validation. No objection handling.",
          },
          { label: "Visibility", desc: "Badge visible in learner profile." },
        ],
        leadershipBrief:
          "Certification logic defined: Product Ed certifies 'Knowledge' (via quizzes), while Sales Enablement certifies 'Ability' (via pitch reviews). This logic is now ready to be configured in ALM.",
      },
    ],
  },
];
